import { useStore } from '@/store'
import { filterLocaisCentro } from '@acme/common'

type Dependencies =
    NonNullable<Parameters<typeof filterLocaisCentro>[2]> extends (
        item: unknown,
    ) => infer TDependencies
        ? TDependencies
        : never

/**
 * Resolve o id do funcionário de uma linha de relatório. Só aceita campos que
 * são de fato do funcionário — o `id` da própria linha costuma ser da linha
 * (registro, dia, espelho), então usá-lo faria o filtro esconder tudo.
 * Devolve `null` quando não é possível saber, e aí a linha passa.
 */
function idDoFuncionario(item: any, funcionario: any): number | null {
    const candidato =
        funcionario?.id ??
        funcionario?.funcionarioId ??
        item?.funcionarioId ??
        item?.Funcionario?.id

    const id = Number(candidato)
    return Number.isFinite(id) && id > 0 ? id : null
}

/**
 * Filtra os dados pelo escopo guardado no useStore: centros de custo, locais e
 * — quando houver seleção — funcionários.
 */
export function useFilterLocaisCentroStore<T = any>(
    accessor?: (item: T) => Dependencies,
) {
    const centroCustoId = useStore(state => state.costCenterId)
    const centroCustoIds = useStore(state => state.costCenterIds)
    const locaisStore = useStore(state => state.locaisIds)
    const employeeIds = useStore(state => state.employeeIds)

    const centros = centroCustoIds.length ? centroCustoIds : [centroCustoId]

    const filterBase = filterLocaisCentro<T>(locaisStore, centros, accessor)

    return (item: T) => {
        if (!filterBase(item)) return false
        if (!employeeIds?.length) return true

        const funcionario = accessor
            ? accessor(item)
            : ((item as any).Funcionario ?? item)
        const id = idDoFuncionario(item, funcionario)

        return id === null ? true : employeeIds.includes(id)
    }
}
